import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { AutocompleteList } from '../../components/AutocompleteList/AutocompleteList';
import { useFormSuggestions } from '../../hooks/useFormSuggestions';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { AddressSuggestion } from '../../types';
import { useFormValidationContainer } from './FormValidatorContainer';
import { InputAlertMessage } from './InputAlertMessage';
import { halfRow, InputGrid } from './InputGrid';
import './InvoiceAddressInput.css';
import { TextInput } from './TextInput';

export type InvoiceAddressModel = {
	orgnr: string;
	company: string;
	street: string;
	careOf: string;
	ref1: string;
	zip: string;
	city: string;
};

export const InvoiceAddressInput = ({ onBlur }: { onBlur?: (e: ChangeEvent) => void }): JSX.Element => {
	const t = useTranslation().form;
	const tAccount = useTranslation().accountPage;
	const { asZip } = useFormatting();
	const [dobIsFocused, setDobIsFocused] = useState(false);
	const [invoiceZipIsFocused, setInvoiceZipIsFocused] = useState(false);
	const formValidation = useFormValidationContainer();
	const [showCareOfInput, setShowCareOfInput] = useState(!!formValidation.paymentCareOf.value);

	const {
		checkCompany,
		companySearchResults,
		setCompanySearchResults,
		addressSuggestions,
		getAddressSuggestions,
		filterUniqueZip,
	} = useFormSuggestions();

	const hasCheckedCompany = useRef(false);
	useEffect(() => {
		if (formValidation.paymentCompany.value.length > 3 && !hasCheckedCompany.current) {
			hasCheckedCompany.current = true;
			checkCompany(formValidation.paymentCompany.value).then((res) => setCompanySearchResults(res));
		}
	}, [formValidation.paymentCompany.value]);

	const showAddressSuggestions = addressSuggestions.length > 0 && invoiceZipIsFocused;

	const showOrgSuggestions =
		companySearchResults?.results &&
		companySearchResults?.results.length > 0 &&
		dobIsFocused &&
		formValidation.paymentOrgnr.value === '';

	async function handleCompanyBlur(inputValue: string) {
		if (inputValue.length < 3) return;
		setCompanySearchResults(await checkCompany(inputValue));
	}

	return (
		<InputGrid>
			<TextInput
				id="invoice-company"
				autocomplete="organization"
				placeholder={tAccount.companyName}
				value={formValidation.paymentCompany.value}
				onChange={formValidation.paymentCompany.handleOnChange}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					formValidation.paymentCompany.validate();
					handleCompanyBlur(e.target.value);
				}}
				isValid={formValidation.paymentCompany.isValid}
				status={formValidation.paymentCompany.type}
			/>
			{formValidation.paymentCompany.type !== 'success' && (
				<InputAlertMessage
					type={formValidation.paymentCompany.type}
					message={formValidation.paymentCompany.message}
				/>
			)}

			<TextInput
				id="invoice-dob"
				placeholder={
					formValidation.paymentOrgnr.isRequired ? t.organisationNumber : t.organisationNumberOptional
				}
				value={formValidation.paymentOrgnr.value}
				onChange={formValidation.paymentOrgnr.handleOnChange}
				onFocus={() => setDobIsFocused(true)}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					formValidation.paymentOrgnr.validate();
					setDobIsFocused(false);
				}}
				autocomplete="off"
				isValid={formValidation.paymentOrgnr.isValid}
				status={formValidation.paymentOrgnr.type}
			/>
			<div>
				{formValidation.paymentOrgnr.type !== 'success' && (
					<InputAlertMessage
						type={formValidation.paymentOrgnr.type}
						message={formValidation.paymentOrgnr.message}
					/>
				)}
			</div>

			{showOrgSuggestions && companySearchResults && (
				<div id="dob-suggestions-list">
					<AutocompleteList
						items={companySearchResults.results}
						template={companySearchResults.results.map((res) => `${res.orgNr} ${res.name}`)}
						onComplete={(item) => {
							formValidation.paymentOrgnr.setValueAndValidate(item.orgNr);
						}}
						display={showOrgSuggestions}
						className={`dob-suggestions invoice ${
							formValidation.paymentOrgnr.isValid ? 'no-validation-message' : 'validation-message'
						}`}
					/>
				</div>
			)}
			<TextInput
				id="invoice-street"
				autocomplete="street-address"
				placeholder={t.street}
				value={formValidation.paymentStreet.value}
				isValid={formValidation.paymentStreet.isValid}
				status={formValidation.paymentStreet.type}
				onChange={formValidation.paymentStreet.handleOnChange}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					e.target.value.length > 0 && e.target.value !== ''
						? getAddressSuggestions(e.target.value)
						: undefined;

					formValidation.paymentStreet.validate();
				}}
				toggleableLabel={
					!showCareOfInput
						? {
								text: t.addCareOf,
								onClick: () => setShowCareOfInput(true),
								id: 'care-of-toggle',
							}
						: undefined
				}
			/>
			{formValidation.paymentStreet.type !== 'success' && (
				<InputAlertMessage
					type={formValidation.paymentStreet.type}
					message={formValidation.paymentStreet.message}
				/>
			)}
			{showCareOfInput && (
				<TextInput
					id="invoice-careof"
					placeholder={t.careOf}
					value={formValidation.paymentCareOf.value}
					autoFocus={showCareOfInput}
					onChange={formValidation.paymentCareOf.handleOnChange}
					onBlur={(e) => {
						if (onBlur) onBlur(e);
					}}
					toggleableLabel={{
						text: t.removeCareOf,
						onClick: () => {
							setShowCareOfInput(false);
							formValidation.paymentCareOf.setValue('');
						},
						id: 'care-of-toggle-remove',
					}}
				/>
			)}
			<TextInput
				className={halfRow}
				id="invoice-zip"
				autocomplete="billing postal-code"
				placeholder={t.zip}
				value={formValidation.paymentZip.value}
				onFocus={(_inputValue) => {
					setInvoiceZipIsFocused(true);
					if (formValidation.paymentStreet.value && formValidation.paymentStreet.value.length > 0) {
						getAddressSuggestions(formValidation.paymentStreet.value);
					}
				}}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					formValidation.paymentZip.validate();
					setInvoiceZipIsFocused(false);
				}}
				isValid={formValidation.paymentZip.isValid}
				status={formValidation.paymentZip.type}
				onChange={formValidation.paymentZip.handleOnChange}
			/>
			<TextInput
				className={halfRow}
				id="invoice-city"
				autocomplete="address-level2"
				isValid={formValidation.paymentCity.isValid}
				status={formValidation.paymentCity.type}
				placeholder={t.city}
				value={formValidation.paymentCity.value}
				onChange={formValidation.paymentCity.handleOnChange}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					formValidation.paymentCity.validate();
				}}
			/>
			<div className={halfRow}>
				{formValidation.paymentZip.type !== 'success' && (
					<InputAlertMessage
						type={formValidation.paymentZip.type}
						message={formValidation.paymentZip.message}
					/>
				)}
			</div>
			<div className={halfRow}>
				{formValidation.paymentCity.type !== 'success' && (
					<InputAlertMessage
						type={formValidation.paymentCity.type}
						message={formValidation.paymentCity.message}
					/>
				)}
			</div>
			{addressSuggestions.length > 0 && (
				<div id="address-suggestions-list" className="grid-halv-row">
					<AutocompleteList
						items={filterUniqueZip(addressSuggestions, formValidation.paymentZip.value) as object[]}
						template={filterUniqueZip(addressSuggestions, formValidation.paymentZip.value)!.map(
							(address) => `${asZip(address.postcode)} ${address.locality}`
						)}
						onComplete={(item: AddressSuggestion) => {
							formValidation.paymentZip.setValueAndValidate(asZip(item.postcode));
							formValidation.paymentCity.setValueAndValidate(item.locality);
						}}
						display={showAddressSuggestions}
						className={`address-suggestions ${
							formValidation.paymentZip.isValid && formValidation.paymentCity.isValid
								? 'no-validation-message'
								: 'validation-message'
						}`}
					/>
				</div>
			)}
			<TextInput
				id="invoice-reference"
				placeholder={t.reference}
				value={formValidation.paymentRef1.value}
				onChange={formValidation.paymentRef1.handleOnChange}
				isValid={formValidation.paymentRef1.isValid}
				status={formValidation.paymentRef1.type}
				onBlur={(e) => {
					if (onBlur) onBlur(e);
					formValidation.paymentRef1.validate();
				}}
			/>
			{formValidation.paymentRef1.type !== 'success' && (
				<InputAlertMessage
					type={formValidation.paymentRef1.type}
					message={formValidation.paymentRef1.message}
				/>
			)}
		</InputGrid>
	);
};
